import { FC, InputHTMLAttributes } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import LockIcon from '@material-ui/icons/Lock';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../utils/formatDate';
import StatusBadge from './StatusBadge';
import {
  THREAD_STATUSES,
  canDeleteThread,
  isMergeable,
  isMergeLockedSource,
  type RequirementThread,
  type ThreadStatus,
} from './types';

interface ThreadTableProps {
  threads: RequirementThread[];
  loading: boolean;
  statusFilter: ThreadStatus | '';
  onStatusFilterChange: (status: ThreadStatus | '') => void;
  /** Case-insensitive name/external_ref search. */
  search: string;
  onSearchChange: (search: string) => void;
  /** Whether SPLIT/MERGED terminal threads are shown. */
  includeTerminal: boolean;
  onIncludeTerminalChange: (include: boolean) => void;
  /** Selected thread ids (for Merge). */
  selectedIds: Set<string>;
  /** Count of selected threads still present & mergeable — drives the Merge button. */
  mergeableCount: number;
  onToggleSelect: (threadId: string) => void;
  /** Start a Merge of the currently selected threads (>= 2). */
  onMerge: () => void;
  /** Submit a DRAFT thread (DRAFT → WIP). */
  onSubmit: (threadId: string) => void;
  /** Start a Split of a WIP thread. */
  onSplit: (threadId: string) => void;
  onOpen: (threadId: string) => void;
  onDelete: (thread: RequirementThread) => void;
  onRefresh: () => void;
  /** The role may submit, split and merge (collaborator and above). */
  canWrite: boolean;
  /** The role may delete (maintainer and above). */
  canApprove: boolean;
  /** Total threads matching current filter (used for pagination). */
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

// Only WIP threads can be merged. The checkbox is shown for those rows only.
const useStyles = makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 800,
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  filter: { minWidth: 200 },
  spacer: { flexGrow: 1 },
  mergeLocked: { marginLeft: theme.spacing(0.5) },
  name: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontWeight: 600,
    '&:hover': { textDecoration: 'underline' },
  },
  idCell: { color: theme.palette.text.secondary, fontSize: 12 },
  empty: { padding: theme.spacing(4), textAlign: 'center', color: '#8993a4' },
}));

export const ThreadTable: FC<ThreadTableProps> = ({
  threads,
  loading,
  statusFilter,
  onStatusFilterChange,
  search,
  onSearchChange,
  includeTerminal,
  onIncludeTerminalChange,
  selectedIds,
  mergeableCount,
  onToggleSelect,
  onMerge,
  onSubmit,
  onSplit,
  onOpen,
  onDelete,
  onRefresh,
  canWrite,
  canApprove,
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('requirement-lifecycle');

  return (
    <Box>
      <Box className={classes.toolbar}>
        <FormControl className={classes.filter} size="small">
          <InputLabel>{t('s01.filter.label')}</InputLabel>
          <Select
            value={statusFilter}
            onChange={e =>
              onStatusFilterChange(e.target.value as ThreadStatus | '')
            }
          >
            <MenuItem value="">{t('s01.filter.all')}</MenuItem>
            {THREAD_STATUSES.map(s => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label={t('s01.search.label')}
          placeholder={t('s01.search.placeholder')}
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          inputProps={{ 'data-testid': 'thread-search' }}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={includeTerminal}
              onChange={e => onIncludeTerminalChange(e.target.checked)}
              inputProps={
                {
                  'data-testid': 'include-terminal',
                } as InputHTMLAttributes<HTMLInputElement>
              }
            />
          }
          label={t('s01.includeTerminal')}
        />
        <span className={classes.spacer} />
        {canWrite && (
          <Button
            size="small"
            onClick={onMerge}
            disabled={loading || mergeableCount < 2}
          >
            {t('s01.merge', { count: mergeableCount })}
          </Button>
        )}
        <Button
          startIcon={<RefreshIcon />}
          onClick={onRefresh}
          disabled={loading}
          size="small"
        >
          {t('s01.refresh')}
        </Button>
      </Box>

      <Table size="small" style={{ tableLayout: 'fixed', minWidth: 800 }}>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" style={{ width: 48 }} />
            <TableCell style={{ width: 80 }}>{t('s01.table.id')}</TableCell>
            <TableCell>{t('s01.table.name')}</TableCell>
            <TableCell style={{ width: 140 }}>
              {t('s01.table.status')}
            </TableCell>
            <TableCell style={{ width: 120 }}>
              {t('s01.table.externalRef')}
            </TableCell>
            <TableCell style={{ width: 160 }}>
              {t('s01.table.updated')}
            </TableCell>
            <TableCell align="right" style={{ width: 120 }}>
              {t('s01.table.actions')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {threads.map(thread => {
            const mergeable = isMergeable(thread);
            return (
              <TableRow key={thread.threadId} hover>
                <TableCell padding="checkbox">
                  {canWrite && mergeable && (
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(thread.threadId)}
                      onChange={() => onToggleSelect(thread.threadId)}
                      inputProps={{ 'aria-label': `select ${thread.threadId}` }}
                    />
                  )}
                </TableCell>
                <TableCell className={classes.idCell}>
                  {thread.threadId}
                </TableCell>
                <TableCell>
                  <span
                    className={classes.name}
                    onClick={() => onOpen(thread.threadId)}
                    role="link"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter') onOpen(thread.threadId);
                    }}
                  >
                    {thread.name || `#${thread.threadId}`}
                  </span>
                </TableCell>
                <TableCell>
                  <StatusBadge status={thread.status} />
                  {isMergeLockedSource(thread) && (
                    <Chip
                      size="small"
                      icon={<LockIcon fontSize="small" />}
                      label={t('s01.mergeLocked')}
                      className={classes.mergeLocked}
                    />
                  )}
                </TableCell>
                <TableCell>{thread.externalRef || '—'}</TableCell>
                <TableCell>{formatDateTime(thread.updatedAt)}</TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => onOpen(thread.threadId)}>
                    {t('s01.rowActions.open')}
                  </Button>
                  {canWrite && thread.status === 'DRAFT' && (
                    <Button
                      size="small"
                      disabled={loading}
                      onClick={() => onSubmit(thread.threadId)}
                    >
                      {t('s01.rowActions.submit')}
                    </Button>
                  )}
                  {canWrite && mergeable && (
                    <Button
                      size="small"
                      disabled={loading}
                      onClick={() => onSplit(thread.threadId)}
                    >
                      {t('s01.rowActions.split')}
                    </Button>
                  )}
                  {canApprove && canDeleteThread(thread) && (
                    <IconButton
                      size="small"
                      aria-label={t('s01.rowActions.delete')}
                      onClick={() => onDelete(thread)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {loading && (
        <Box style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {!loading && threads.length === 0 && (
        <Typography className={classes.empty}>{t('common.empty')}</Typography>
      )}
      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[25, 50, 100]}
        onPageChange={(_e, p) => onPageChange(p)}
        onRowsPerPageChange={e => {
          onPageSizeChange(Number(e.target.value));
          onPageChange(0);
        }}
      />
    </Box>
  );
};

export default ThreadTable;
